
import React, { useState, useCallback, useMemo } from 'react';
import { CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { useScheduleVisualization } from "@/hooks/useScheduleVisualization";
import { toast } from 'sonner';
import AgendaHeader from './global-agenda/AgendaHeader';
import CalendarSection from './global-agenda/CalendarSection';
import DetailsPanel from './global-agenda/DetailsPanel';
import TimeBlockModal from './global-agenda/TimeBlockModal';
import { Global24hAgendaTabProps } from './global-agenda/types';

// Debug mode controlled by environment or prop
const DEBUG_MODE = process.env.NODE_ENV === 'development' && false; // Disable by default

const Global24hAgendaTab: React.FC<Global24hAgendaTabProps> = ({ psychologistId }) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Use the optimized hook
  const { 
    daySchedules, 
    isLoading, 
    loadScheduleData, 
    getScheduleForDate 
  } = useScheduleVisualization(psychologistId);

  // Stable callbacks to prevent re-renders
  const handleDateSelect = useCallback((date: Date) => {
    console.log('📅 Data selecionada:', format(date, 'yyyy-MM-dd'));
    setSelectedDate(date);
  }, []);

  const handleConfigureClick = useCallback(() => {
    if (!selectedDate) {
      toast.error('Selecione uma data primeiro');
      return;
    }
    setIsModalOpen(true);
  }, [selectedDate]);

  const handleModalSuccess = useCallback(() => {
    // Reload data after saving
    if (selectedDate) {
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      loadScheduleData(startOfMonth, endOfMonth);
    }
  }, [selectedDate, loadScheduleData]);

  // Callback para recarregar dados após deleção
  const handleScheduleUpdate = useCallback(() => {
    console.log('🔄 [Global24hAgendaTab] Recarregando dados após deleção');
    if (selectedDate) {
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      loadScheduleData(startOfMonth, endOfMonth);
    }
  }, [selectedDate, loadScheduleData]);

  // Memoize selected date schedule calculation
  const selectedDateSchedule = useMemo(() => {
    if (!selectedDate) return null;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const schedule = getScheduleForDate(dateStr);
    
    if (DEBUG_MODE) {
      console.log('📋 Selected date schedule:', {
        date: dateStr,
        schedule,
        hasSchedule: schedule?.hasSchedule
      });
    }
    
    return schedule;
  }, [selectedDate, getScheduleForDate]);

  // Debug info only in debug mode
  const debugInfo = useMemo(() => {
    if (!DEBUG_MODE) return null;
    
    return {
      psychologistId,
      currentMonth: selectedDate ? format(selectedDate, 'yyyy-MM') : undefined,
      scheduleDataSize: daySchedules.size,
      selectedDate,
      hasSchedule: selectedDateSchedule?.hasSchedule || false
    };
  }, [psychologistId, selectedDate, daySchedules.size, selectedDateSchedule]);

  if (!psychologistId) {
    console.warn('⚠️ [Global24hAgendaTab] psychologistId não fornecido!');
  }

  return (
    <div className="space-y-6">
      {/* Debug panel only in debug mode */}
      {DEBUG_MODE && debugInfo && (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h4 className="font-bold text-yellow-800">Debug Info</h4>
          <pre className="text-xs text-yellow-700 mt-2">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      )}

      <AgendaHeader psychologistId={psychologistId} />

      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Section */}
          <CalendarSection
            psychologistId={psychologistId}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onConfigureClick={handleConfigureClick}
          />

          {/* Details Panel */}
          <DetailsPanel
            selectedDate={selectedDate}
            schedule={selectedDateSchedule}
            onEditClick={handleConfigureClick}
            onScheduleUpdate={handleScheduleUpdate}
            psychologistId={psychologistId}
          />
        </div>
      </CardContent>

      {/* Time Block Modal */}
      <TimeBlockModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedDate={selectedDate}
        psychologistId={psychologistId}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

// Memoize the entire component to prevent unnecessary re-renders
export default React.memo(Global24hAgendaTab);
